/**
 * SharedRenderer - Single WebGL context shared across the app
 *
 * Avoids creating multiple WebGL contexts (mobile browsers limit to ~8-16).
 * Features register a "view" with their own Scene + Camera, and the shared
 * renderer draws each active view every frame. When no views are registered
 * the render loop pauses automatically (zero GPU cost).
 *
 * Usage:
 *   const sr = SharedRenderer.instance;
 *   const viewId = sr.addView({ scene, camera, container });
 *   // ... later
 *   sr.removeView(viewId);
 */

import * as THREE from 'three';

export type RenderQuality = 'low' | 'medium' | 'high';

export interface SharedRendererConfig {
  quality?: RenderQuality;
  maxDpr?: number;
}

export interface ViewOptions {
  scene: THREE.Scene;
  camera: THREE.Camera;
  /** DOM element the view renders into. Canvas is sized to match. */
  container: HTMLElement;
  /** Priority for render order (lower = first). Default 0. */
  priority?: number;
  /** Called every frame before this view is rendered. */
  onBeforeRender?: () => void;
  /**
   * Externally driven view. When true, this view is NOT drawn by the
   * SharedRenderer's own rAF loop — its owner renders it (e.g. the in-game
   * Engine game loop, which must render AFTER physics interpolation in the
   * same frame). Leaving this false for such a view double-renders the scene
   * every frame (Engine loop + this loop).
   */
  manual?: boolean;
}

interface ViewEntry extends Required<Pick<ViewOptions, 'priority'>> {
  id: string;
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera | THREE.OrthographicCamera;
  container: HTMLElement;
  onBeforeRender?: () => void;
  manual: boolean;
}

export const QUALITY_SETTINGS: Record<RenderQuality, {
  shadowMapSize: number;
  maxDpr: number;
  antialias: boolean;
  walletModel: 'low' | 'medium' | 'high';
  billSegments: number;
  billShadows: boolean;
  billTextureSize: number;
}> = {
  low: {
    shadowMapSize: 512,
    maxDpr: 1,
    antialias: false,
    walletModel: 'low',
    billSegments: 4,
    billShadows: false,
    billTextureSize: 256,
  },
  medium: {
    shadowMapSize: 1024,
    maxDpr: 1.5,
    antialias: true,
    walletModel: 'medium',
    billSegments: 6,
    billShadows: true,
    billTextureSize: 512,
  },
  high: {
    // 2048 — at the close shooting-camera framing, 1024 shows visibly blocky
    // shadow edges (shadow camera covers a ~30-unit area, so 1024 ≈ 34 texels/
    // unit). 2048 doubles texel density and matches the pre-session look. This
    // is re-applied to the served scene's lights after load (see main.ts), so it
    // takes effect even though the scene document authors its own size.
    shadowMapSize: 2048,
    maxDpr: 2,
    antialias: true,
    walletModel: 'high',
    billSegments: 8,  // matches the pre-tier hardcoded value — no regression for the default 'high' tier
    billShadows: true,
    billTextureSize: 1024,
  },
};

let nextViewId = 0;

export class SharedRenderer {
  private static _instance: SharedRenderer | null = null;

  readonly gl: THREE.WebGLRenderer;

  private quality: RenderQuality;
  private maxDpr: number;
  private views = new Map<string, ViewEntry>();
  private sortedViews: ViewEntry[] = [];
  private animationId: number | null = null;
  private isVisible = true;
  private boundTick = this.tick.bind(this);

  /** Get or create the singleton. Call init() once at app start. */
  static get instance(): SharedRenderer {
    if (!SharedRenderer._instance) {
      throw new Error('SharedRenderer not initialized. Call SharedRenderer.init() first.');
    }
    return SharedRenderer._instance;
  }

  static init(config: SharedRendererConfig = {}): SharedRenderer {
    if (SharedRenderer._instance) return SharedRenderer._instance;
    SharedRenderer._instance = new SharedRenderer(config);
    return SharedRenderer._instance;
  }

  private constructor(config: SharedRendererConfig) {
    this.quality = config.quality ?? 'high';
    const settings = QUALITY_SETTINGS[this.quality];
    this.maxDpr = config.maxDpr ?? settings.maxDpr;

    // Create the one and only WebGL renderer
    // Canvas starts detached; it's moved into view containers as needed
    this.gl = new THREE.WebGLRenderer({
      antialias: settings.antialias,
      powerPreference: 'high-performance',
      stencil: false,
      depth: true,
      alpha: true,
    });

    this.gl.setPixelRatio(Math.min(window.devicePixelRatio, this.maxDpr));
    this.gl.outputColorSpace = THREE.SRGBColorSpace;
    this.gl.toneMapping = THREE.ACESFilmicToneMapping;
    this.gl.toneMappingExposure = 1.0;
    this.gl.shadowMap.enabled = true;
    this.gl.shadowMap.type = THREE.PCFSoftShadowMap;
    this.gl.autoClear = false;

    // Pause rendering when tab is hidden
    document.addEventListener('visibilitychange', () => {
      this.isVisible = !document.hidden;
    });
  }

  /**
   * Register a view to be rendered each frame.
   * The shared canvas is appended to `container` and sized to fit.
   */
  addView(opts: ViewOptions): string {
    const id = `view_${nextViewId++}`;
    const entry: ViewEntry = {
      id,
      scene: opts.scene,
      camera: opts.camera as THREE.PerspectiveCamera,
      container: opts.container,
      priority: opts.priority ?? 0,
      onBeforeRender: opts.onBeforeRender,
      manual: opts.manual ?? false,
    };

    this.views.set(id, entry);
    this.rebuildSortedViews();

    // Always attach canvas to the highest-priority view's container
    const activeView = this.sortedViews[this.sortedViews.length - 1];
    this.attachCanvasTo(activeView.container);
    this.resizeToView(activeView);

    // Start loop only when there's a view for it to draw. Manual views are
    // rendered by their owner's loop (the Engine), so a manual-only registry
    // would just burn an idle rAF every frame.
    if (this.hasAutoView()) this.startLoop();

    return id;
  }

  /** Remove a view. Loop pauses when no auto views remain. */
  removeView(id: string): void {
    this.views.delete(id);
    this.rebuildSortedViews();

    if (!this.hasAutoView()) this.stopLoop();

    if (this.views.size === 0) {
      // Detach canvas
      this.gl.domElement.remove();
    } else {
      this.attachCanvasTo(this.sortedViews[0].container);
    }
  }

  /** Check if a view exists. */
  hasView(id: string): boolean {
    return this.views.has(id);
  }

  /** Get the canvas element. */
  get canvas(): HTMLCanvasElement {
    return this.gl.domElement;
  }

  /** Current quality level. */
  getQuality(): RenderQuality {
    return this.quality;
  }

  /** Get betting-specific quality settings. */
  getBettingQualitySettings() {
    const s = QUALITY_SETTINGS[this.quality];
    return {
      walletModel: s.walletModel,
      billSegments: s.billSegments,
      billShadows: s.billShadows,
      billTextureSize: s.billTextureSize,
    };
  }

  /** Change render quality. */
  setQuality(quality: RenderQuality): void {
    this.quality = quality;
    const settings = QUALITY_SETTINGS[quality];
    this.maxDpr = settings.maxDpr;
    this.gl.setPixelRatio(Math.min(window.devicePixelRatio, this.maxDpr));
  }

  /** Force a resize for a specific view. */
  resizeToView(entry: ViewEntry): void {
    const w = entry.container.clientWidth;
    const h = entry.container.clientHeight;
    if (w === 0 || h === 0) return;

    const dpr = Math.min(window.devicePixelRatio, this.maxDpr);
    this.gl.setPixelRatio(dpr);
    this.gl.setSize(w, h, false);

    // Update canvas CSS size
    this.gl.domElement.style.width = `${w}px`;
    this.gl.domElement.style.height = `${h}px`;

    if ((entry.camera as THREE.PerspectiveCamera).isPerspectiveCamera) {
      const cam = entry.camera as THREE.PerspectiveCamera;
      cam.aspect = w / h;
      cam.updateProjectionMatrix();
    }
  }

  // ── Private ──────────────────────────────────────────

  private attachCanvasTo(container: HTMLElement): void {
    const canvas = this.gl.domElement;
    if (canvas.parentElement !== container) {
      canvas.style.display = 'block';
      canvas.style.width = '100%';
      canvas.style.height = '100%';
      container.appendChild(canvas);
    }
  }

  private rebuildSortedViews(): void {
    this.sortedViews = [...this.views.values()].sort((a, b) => a.priority - b.priority);
  }

  private startLoop(): void {
    if (this.animationId !== null) return;
    this.animationId = requestAnimationFrame(this.boundTick);
  }

  private stopLoop(): void {
    if (this.animationId !== null) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
  }

  private tick(): void {
    // Render the highest-priority view that is NOT externally driven.
    // "manual" views (the in-game Renderer) are drawn by their owner's own
    // loop — the Engine game loop, after physics interpolation — so drawing
    // them here too would render the scene twice per frame.
    const v = this.activeAutoView();
    if (!v) {
      // No auto view left to draw — park the loop. addView() re-arms it
      // the next time an auto view registers.
      this.animationId = null;
      return;
    }

    if (this.isVisible) {
      // Move canvas to the active view's container if needed
      this.attachCanvasTo(v.container);
      this.resizeIfNeeded(v);
      v.onBeforeRender?.();
      this.gl.clear();
      this.gl.render(v.scene, v.camera);
    }

    this.animationId = requestAnimationFrame(this.boundTick);
  }

  /** True when at least one registered view is drawn by this loop. */
  private hasAutoView(): boolean {
    return this.sortedViews.some((v) => !v.manual);
  }

  /**
   * Highest-priority view this loop is responsible for rendering.
   * Excludes `manual` views, which are driven by their owner's render loop.
   */
  private activeAutoView(): ViewEntry | null {
    for (let i = this.sortedViews.length - 1; i >= 0; i--) {
      if (!this.sortedViews[i].manual) return this.sortedViews[i];
    }
    return null;
  }

  private lastW = 0;
  private lastH = 0;

  private resizeIfNeeded(entry: ViewEntry): void {
    const w = entry.container.clientWidth;
    const h = entry.container.clientHeight;
    if (w === this.lastW && h === this.lastH) return;
    this.lastW = w;
    this.lastH = h;
    this.resizeToView(entry);
  }

  /** Dispose everything. Only call on app shutdown. */
  dispose(): void {
    this.stopLoop();
    this.views.clear();
    this.sortedViews = [];
    this.gl.dispose();
    this.gl.domElement.remove();
    SharedRenderer._instance = null;
  }
}
